import React from 'react';
import { observer } from 'mobx-react-lite';
import { PlusIcon, ChevronLeftIcon, Cross1Icon, TrashIcon } from '@radix-ui/react-icons';
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  MouseSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { IconButton } from '@producer/ui-kit';
import { PropertyType } from '@producer/properties';

import styles from './properties-panel.module.scss';

import { Property } from '../../../app/entities/property';
import { DeleteDialog } from '../../../components/dialogs/DeleteDialog';
import { openModal } from '../../../core/modal/open-modal';
import { uiStore } from '../../../core/stores/ui-store';
import { PropertyTypeSelector } from './property-type-selector';
import { SortableOptionItem } from './sortable-option-item';

import { HistoryInput } from '../../../components/history-input/history-input';

interface EditPropertyPanelProps {
  property: Property;
  onBackToList: () => void;
}

export const EditPropertyPanel: React.FC<EditPropertyPanelProps> = observer(
  ({ property, onBackToList }: EditPropertyPanelProps) => {
    const handleDeleteProperty = async () => {
      try {
        await property.delete();
        onBackToList();
      } catch (error) {
        console.error('Failed to delete property:', error);
      }
    };

    const handleShowDeleteDialog = () => {
      openModal(DeleteDialog, {
        title: 'Delete property?',
        text: `Are you sure you want to delete "${property.name}"? This action cannot be undone.`,
        onSubmit: handleDeleteProperty,
        actionText: 'Delete',
      });
    };

    const handleDragEnd = async (event: DragEndEvent) => {
      const { active, over } = event;

      // todo: currently the id is the index of the option, this should be changed to a unique identifier

      if (over && active.id !== over.id) {
        const oldIndex = parseInt(active.id.toString());
        const newIndex = parseInt(over.id.toString());

        const sortedOptions = arrayMove(property.options, oldIndex, newIndex);

        property.update({ options: sortedOptions });
      }
    };

    const sensors = useSensors(
      useSensor(MouseSensor, {
        activationConstraint: {
          distance: 8,
        },
      }),
    );

    const handleAddOption = async () => {
      const newOption = `Option ${property.options.length + 1}`;
      try {
        await property.update({ options: [...property.options, newOption] });
      } catch (error) {
        console.error('Failed to add option:', error);
      }
    };

    const showOptionsSection =
      property.type === PropertyType.SingleSelect || property.type === PropertyType.MultiSelect;

    return (
      <div className={styles.Container}>
        <div className={styles.PanelHeader}>
          <div className={styles.PanelHeaderTitle}>
            <div onClick={onBackToList}>
              <ChevronLeftIcon />
            </div>
            <span>Edit property</span>
          </div>

          <IconButton
            size={'small'}
            icon={<Cross1Icon />}
            onClick={() => uiStore.closePropertiesPanel()}
          />
        </div>

        <div className={styles.EditContent}>
          <div className={styles.EditForm}>
            <HistoryInput value={property.name} onChange={(name) => property.update({ name })} />

            <PropertyTypeSelector
              value={property.type}
              onChange={(value) => property.update({ type: value as PropertyType })}
            />

            {showOptionsSection && (
              <div className={styles.OptionsSection}>
                <div className={styles.OptionsHeader}>
                  <p>Options</p>
                  <button className={styles.AddOptionButton} onClick={handleAddOption}>
                    <PlusIcon />
                  </button>
                </div>

                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={property.options.map((_, index) => index.toString())}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className={styles.OptionsList}>
                      {property.options.length === 0 ? (
                        <div className={styles.EmptyOptions}>
                          <span>No options added yet. Click the + button to add options.</span>
                        </div>
                      ) : (
                        property.options.map((option, index) => (
                          <SortableOptionItem
                            key={index}
                            id={index.toString()}
                            index={index}
                            value={option}
                            property={property}
                          />
                        ))
                      )}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            )}
          </div>
        </div>

        <div className={styles.PanelFooter}>
          <div
            className={`${styles.MenuItem} ${styles.DeleteMenuItem}`}
            onClick={handleShowDeleteDialog}
          >
            <TrashIcon />
            <span>Delete property</span>
          </div>
        </div>
      </div>
    );
  },
);
